const text = (value) => String(value ?? '').trim();

function arrayValue(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseIdentifierContainer(value) {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!raw || !/^[{[]/.test(raw)) return value;
  try {
    return JSON.parse(raw);
  } catch {
    return value;
  }
}

function nestedIdentifierCandidates(value, depth = 0) {
  if (depth > 4 || value === null || value === undefined) return [];
  const parsed = parseIdentifierContainer(value);
  if (parsed !== value) return nestedIdentifierCandidates(parsed, depth + 1);
  if (typeof value === 'string' || typeof value === 'number') return [text(value)].filter(Boolean);
  if (Array.isArray(value)) return value.flatMap((entry) => nestedIdentifierCandidates(entry, depth + 1));
  if (typeof value !== 'object') return [];
  return Object.values(value).flatMap((entry) => nestedIdentifierCandidates(entry, depth + 1));
}

function activeValue(value) {
  return value !== false && value !== 0 && value !== '0';
}

export function normalizeNikkeId(value) {
  const raw = text(value).toLowerCase();
  const match = raw.match(/^c?(\d{1,4})$/);
  if (!match) return '';
  return `c${String(Number(match[1])).padStart(3, '0')}`;
}

export function nikkeIdFromTid(value) {
  const tid = Number(value);
  if (!Number.isFinite(tid) || tid <= 0) return '';
  const resourceId = Math.floor(Math.trunc(tid) / 100) % 1000;
  return resourceId > 0 ? `c${String(resourceId).padStart(3, '0')}` : '';
}

export function normalizeNikkeLookupKey(value) {
  return text(value).toLocaleLowerCase('ko-KR');
}

export function normalizeNikkeCatalogRow(row = {}) {
  const resourceId = Number(row.resource_id || String(row.nikke_id || '').replace(/\D/g, '')) || null;
  const nikkeId = normalizeNikkeId(row.nikke_id || resourceId);
  const aliases = [...new Set([
    row.alias1,
    row.alias2,
    row.alias,
    ...arrayValue(row.aliases),
  ].map(text).filter(Boolean))];
  const sourceIdentifiers = [...new Set(nestedIdentifierCandidates(
    row.sourceIdentifiers
      ?? row.source_identifiers
      ?? row.identifiers
      ?? row.sourceIdentifier
      ?? row.source_identifier,
  ))];
  return {
    nikkeId,
    resourceId,
    gameCharacterId: row.game_character_id ? String(row.game_character_id) : '',
    nameCode: row.name_code ? String(row.name_code) : '',
    name: text(row.name || row.official_name || nikkeId),
    officialName: text(row.official_name || row.name),
    aliases,
    sourceIdentifiers,
    imageUrl: text(row.image_url),
    rare: text(row.rare || row.rarity),
    className: text(row.class),
    corporation: text(row.corporation),
    element: text(row.element_kr || row.element),
    elementCode: text(row.element_code),
    elementIconKey: text(row.element_icon_key),
    elementImageUrl: text(row.element_image_url),
    burst: text(row.burst),
    burstCode: text(row.burst_code || row.burst_raw),
    burstImageUrl: text(row.burst_image_url),
    weaponType: text(row.weapon_type),
    attackType: text(row.attack_type),
    costumes: arrayValue(row.costumes),
    displayOrder: Number(row.display_order || 0),
    active: activeValue(row.active),
  };
}

function lookupKeys(item) {
  const keys = new Set();
  const add = (value) => {
    const raw = normalizeNikkeLookupKey(value);
    if (raw) keys.add(raw);
  };
  add(item.nikkeId);
  add(item.resourceId);
  add(item.gameCharacterId);
  add(item.nameCode);
  item.sourceIdentifiers.forEach(add);
  add(item.name);
  add(item.officialName);
  item.aliases.forEach(add);
  return keys;
}

function addLookupKey({ byKey, ambiguousKeys }, key, item) {
  if (!key || !item?.nikkeId) return;
  const ambiguous = ambiguousKeys.get(key);
  if (ambiguous) {
    if (!ambiguous.some((entry) => entry.nikkeId === item.nikkeId)) ambiguous.push(item);
    return;
  }
  const existing = byKey.get(key);
  if (!existing) {
    byKey.set(key, item);
    return;
  }
  if (existing.nikkeId === item.nikkeId) return;
  byKey.delete(key);
  ambiguousKeys.set(key, [existing, item]);
}

export function buildNikkeCatalogLookupFromItems(items = []) {
  const activeItems = items.filter((item) => item?.active && item?.nikkeId);
  const byKey = new Map();
  const ambiguousKeys = new Map();
  for (const item of activeItems) {
    for (const key of lookupKeys(item)) addLookupKey({ byKey, ambiguousKeys }, key, item);
  }
  return { items: activeItems, byKey, ambiguousKeys };
}

export function buildNikkeCatalogLookup(rows = []) {
  return buildNikkeCatalogLookupFromItems(rows.map(normalizeNikkeCatalogRow));
}

function objectIdentifierCandidates(value) {
  if (typeof value === 'string' || typeof value === 'number') return [text(value)].filter(Boolean);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];

  const sourceIdentifiers = nestedIdentifierCandidates(
    value.sourceIdentifiers
      ?? value.source_identifiers
      ?? value.identifiers
      ?? value.sourceIdentifier
      ?? value.source_identifier,
  );
  const directIdentifiers = [
    value.nikkeId,
    value.nikke_id,
    value.gameCharacterId,
    value.game_character_id,
    value.characterId,
    value.character_id,
    value.nameCode,
    value.name_code,
    value.resourceId,
    value.resource_id,
    value.tid,
    value.code,
    value.id,
    value.name,
    value.officialName,
    value.official_name,
    value.alias,
    value.characterName,
    value.character_name,
  ].map(text).filter(Boolean);

  return [...new Set([...sourceIdentifiers, ...directIdentifiers])];
}

export function nikkeIdentifierCandidates(value) {
  if (Array.isArray(value)) return value.flatMap(nikkeIdentifierCandidates);
  return objectIdentifierCandidates(value);
}

function catalogMatch(catalog, raw) {
  const byKey = catalog?.byKey;
  if (!byKey) return null;
  const key = normalizeNikkeLookupKey(raw);
  if (!key) return null;
  const direct = byKey.get(key);
  if (direct) return { status: 'resolved', item: direct, key };
  const ambiguous = catalog?.ambiguousKeys?.get(key);
  if (ambiguous?.length) return { status: 'ambiguous', matches: ambiguous, key };

  const normalizedId = normalizeNikkeId(raw);
  if (normalizedId) {
    const byId = byKey.get(normalizedId);
    if (byId) return { status: 'resolved', item: byId, key: normalizedId };
    const ambiguousId = catalog?.ambiguousKeys?.get(normalizedId);
    if (ambiguousId?.length) return { status: 'ambiguous', matches: ambiguousId, key: normalizedId };
  }

  const decorated = key.match(/^c?(\d{1,4})(?:[_:\-].*)$/);
  if (decorated) {
    const decoratedId = normalizeNikkeId(decorated[1]);
    const byDecorated = byKey.get(decoratedId);
    if (byDecorated) return { status: 'resolved', item: byDecorated, key: decoratedId };
    const ambiguousDecorated = catalog?.ambiguousKeys?.get(decoratedId);
    if (ambiguousDecorated?.length) return { status: 'ambiguous', matches: ambiguousDecorated, key: decoratedId };
  }

  if (/^\d{5,}$/.test(key)) {
    const fromTid = nikkeIdFromTid(raw);
    if (fromTid) {
      const byTid = byKey.get(fromTid);
      if (byTid) return { status: 'resolved', item: byTid, key: fromTid };
      const ambiguousTid = catalog?.ambiguousKeys?.get(fromTid);
      if (ambiguousTid?.length) return { status: 'ambiguous', matches: ambiguousTid, key: fromTid };
    }
  }
  return null;
}

export function resolveNikkeInCatalog(catalog, value) {
  const candidates = nikkeIdentifierCandidates(value);
  if (!candidates.length) return { status: 'unresolved', item: null, matches: [], candidates };
  for (const candidate of candidates) {
    const match = catalogMatch(catalog, candidate);
    if (!match) continue;
    if (match.status === 'ambiguous') {
      return { ...match, item: null, candidates };
    }
    return { ...match, matches: [match.item], candidates };
  }
  return { status: 'unresolved', item: null, matches: [], candidates };
}

export function fallbackNikkeIdentifier(value) {
  const raw = text(value);
  if (!raw) return '';
  if (/^c\d{1,4}$/i.test(raw) || /^\d{1,3}$/.test(raw)) return normalizeNikkeId(raw);
  const decorated = raw.toLowerCase().match(/^c?(\d{1,4})(?:[_:\-].*)$/);
  if (decorated) return normalizeNikkeId(decorated[1]);
  if (/^\d{5,}$/.test(raw)) return nikkeIdFromTid(raw) || raw;
  return raw.toLocaleLowerCase('ko-KR');
}

export function canonicalNikkeId(catalog, value, { fallback = false } = {}) {
  const result = resolveNikkeInCatalog(catalog, value);
  if (result.status === 'resolved') return result.item?.nikkeId || '';
  if (!fallback || result.status === 'ambiguous') return '';
  for (const candidate of result.candidates) {
    const normalized = fallbackNikkeIdentifier(candidate);
    if (normalized) return normalized;
  }
  return '';
}

export function findNikkeInCatalog(catalog, value) {
  const result = resolveNikkeInCatalog(catalog, value);
  return result.status === 'resolved' ? result.item : null;
}

export function nikkeCatalogSearchText(item = {}) {
  return [
    item.nikkeId,
    item.resourceId,
    item.gameCharacterId,
    item.nameCode,
    ...(Array.isArray(item.sourceIdentifiers) ? item.sourceIdentifiers : []),
    item.name,
    item.officialName,
    ...(Array.isArray(item.aliases) ? item.aliases : []),
    item.element,
    item.className,
    item.corporation,
    item.weaponType,
  ].map(text).filter(Boolean).join(' ');
}

export function normalizeBossKey(value) {
  return text(value)
    .toLocaleLowerCase('ko-KR')
    .replace(/\[[^\]]*]/g, '')
    .replace(/[^0-9a-z가-힣]/g, '');
}

export function buildBossImageLookup(rows = []) {
  const byKey = new Map();
  for (const row of rows) {
    const key = normalizeBossKey(row.boss_key || row.boss_name);
    if (!key) continue;
    byKey.set(key, {
      id: text(row.id),
      bossName: text(row.boss_name),
      bossKey: key,
      imageUrl: text(row.boss_image_url),
      category: text(row.category),
    });
  }
  return byKey;
}
